import type { OptimizationDatasetNamePayload } from '../utils/optimizationDatasetName';
import { formatOptimizationDatasetNameForApi } from '../utils/optimizationDatasetName';

/** Config tuning (/runner/steps/v2 via optimize jobs) and standalone evaluation. */
export type EvaluationMode =
  'openai-similarity' | 'similarity' | 'bert_score' | 'llm-judge' | 'algorithmic' | 'message-scorer' | 'config-scorer';

export type ConfigTuningEvaluationMode = EvaluationMode;

export type EvaluationScope = 'message' | 'dialog';
export type EvaluationDirection = 'maximize' | 'minimize';
export const DEFAULT_QUALITY_DROP_PCT = 20;

export type LlmJudgeMetricId = 'quality' | 'token' | 'cost';
export type AlgorithmicMetricId = 'response_length';
export type AlgorithmicUnit = 'tokens' | 'words' | 'characters' | 'message_count';
export type AlgorithmicAggregation = 'mean' | 'min' | 'max' | 'sum';
export type DatasetSplit = 'train' | 'validation';

export const DEFAULT_EVALUATION_MODE: EvaluationMode = 'openai-similarity';

export const OPENAI_SIMILARITY_EMBEDDING_MODEL = 'text-embedding-3-small';
export const SIMILARITY_EMBEDDING_MODEL = 'all-mpnet-base-v2';

export const OPENAI_SIMILARITY_EMBEDDING_OPTIONS = [OPENAI_SIMILARITY_EMBEDDING_MODEL] as const;
export const SIMILARITY_EMBEDDING_OPTIONS = [SIMILARITY_EMBEDDING_MODEL] as const;

export const LLM_JUDGE_METRIC_OPTIONS: LlmJudgeMetricId[] = ['quality', 'token', 'cost'];

export const LLM_JUDGE_METRIC_LABELS: Record<LlmJudgeMetricId, string> = {
  quality: 'Quality',
  token: 'Token',
  cost: 'Cost',
};

export const ALGORITHMIC_METRIC_OPTIONS: AlgorithmicMetricId[] = ['response_length'];

export const ALGORITHMIC_METRIC_LABELS: Record<AlgorithmicMetricId, string> = {
  response_length: 'Response length',
};

export type BertScoreMetric = 'P' | 'R' | 'F1';
export type BertScoreLang = 'en' | 'ru';

export const BERT_SCORE_METRIC_OPTIONS: BertScoreMetric[] = ['F1', 'P', 'R'];

export const BERT_SCORE_METRIC_LABELS: Record<BertScoreMetric, string> = {
  F1: 'F1 — balance of precision and recall (recommended)',
  P: 'P — precision (less extra content in the answer)',
  R: 'R — recall (covers more of the reference)',
};

export const BERT_SCORE_LANG_OPTIONS: BertScoreLang[] = ['en', 'ru'];

export const BERT_SCORE_LANG_LABELS: Record<BertScoreLang, string> = {
  en: 'English (en)',
  ru: 'Russian (ru)',
};

export const BERT_SCORE_DEFAULT_MODEL = 'microsoft/deberta-base-mnli';

export const BERT_SCORE_MODEL_OPTIONS = [BERT_SCORE_DEFAULT_MODEL, 'microsoft/deberta-large-mnli'] as const;

export type BertScoreModelType = (typeof BERT_SCORE_MODEL_OPTIONS)[number];

export const BERT_SCORE_MODEL_LABELS: Record<BertScoreModelType, string> = {
  'microsoft/deberta-base-mnli': 'DeBERTa base MNLI — default, faster',
  'microsoft/deberta-large-mnli': 'DeBERTa large MNLI — heavier, potentially more accurate',
};

export const CONFIG_TUNING_EVALUATION_MODES: EvaluationMode[] = [
  'openai-similarity',
  'similarity',
  'bert_score',
  'llm-judge',
  'algorithmic',
  'message-scorer',
  'config-scorer',
];

export const STANDALONE_EVALUATION_MODES = CONFIG_TUNING_EVALUATION_MODES;

/** Backend scorers do not accept frontend evaluator_params. */
export function evaluationModeHasEvaluatorParams(mode: EvaluationMode): boolean {
  return mode !== 'message-scorer' && mode !== 'config-scorer';
}

export const EVALUATION_MODE_LABELS: Record<EvaluationMode, string> = {
  'openai-similarity': 'OpenAI similarity',
  similarity: 'Similarity',
  bert_score: 'BERT score',
  'llm-judge': 'LLM judge',
  algorithmic: 'Algorithmic',
  'message-scorer': 'Message scorer',
  'config-scorer': 'Config scorer',
};

export const DATASET_SPLIT_LABELS: Record<DatasetSplit, string> = {
  train: 'Train',
  validation: 'Validation',
};

/** Flat form state — fields shown depend on evaluation_mode. */
export interface EvaluatorParamsFormState {
  embedding_model_name: string;
  metric_to_use: string;
  lang: string;
  model_type: string;
  prompt: string;
  evaluation_scope: EvaluationScope;
  metric_id: string;
  direction: EvaluationDirection;
  judge_temperature: number;
  judge_max_tokens: string;
  judge_model_name: string;
  unit: AlgorithmicUnit;
  aggregation: AlgorithmicAggregation;
  token_model_name: string;
  normalization_max: string;
  /** Сколько качеству разрешено просесть (%), пока отбор идёт по Token/Cost. 100 = ограничение снято. */
  quality_drop_pct: string;
}

function defaultEmbeddingModelForMode(mode: EvaluationMode): string {
  if (mode === 'similarity') return SIMILARITY_EMBEDDING_MODEL;
  if (mode === 'openai-similarity') return OPENAI_SIMILARITY_EMBEDDING_MODEL;
  return '';
}

function defaultMetricIdForMode(mode: EvaluationMode): string {
  if (mode === 'algorithmic') return ALGORITHMIC_METRIC_OPTIONS[0];
  if (mode === 'llm-judge') return 'quality';
  return 'quality';
}

function defaultAlgorithmicMetric(value: string): AlgorithmicMetricId {
  if (ALGORITHMIC_METRIC_OPTIONS.includes(value as AlgorithmicMetricId)) {
    return value as AlgorithmicMetricId;
  }
  return ALGORITHMIC_METRIC_OPTIONS[0];
}

function defaultBertScoreMetric(value: string): BertScoreMetric {
  if (value === 'P' || value === 'R' || value === 'F1') return value;
  return 'F1';
}

function defaultBertScoreLang(value: string): BertScoreLang {
  if (value === 'en' || value === 'ru') return value;
  return 'en';
}

function defaultBertScoreModel(value: string): BertScoreModelType {
  if ((BERT_SCORE_MODEL_OPTIONS as readonly string[]).includes(value)) {
    return value as BertScoreModelType;
  }
  return BERT_SCORE_DEFAULT_MODEL;
}

export function createDefaultEvaluatorParamsForm(
  mode: EvaluationMode = DEFAULT_EVALUATION_MODE
): EvaluatorParamsFormState {
  return {
    embedding_model_name: defaultEmbeddingModelForMode(mode),
    metric_to_use: mode === 'bert_score' ? 'F1' : '',
    lang: mode === 'bert_score' ? 'en' : '',
    model_type: mode === 'bert_score' ? BERT_SCORE_DEFAULT_MODEL : '',
    prompt: '',
    evaluation_scope: 'message',
    metric_id: defaultMetricIdForMode(mode),
    direction: 'maximize',
    judge_temperature: 0,
    judge_max_tokens: '512',
    judge_model_name: '',
    unit: 'tokens',
    aggregation: 'mean',
    token_model_name: 'gpt-4.1',
    normalization_max: '500',
    quality_drop_pct: String(DEFAULT_QUALITY_DROP_PCT),
  };
}

export function mergeEvaluatorParamsFormForMode(
  mode: EvaluationMode,
  prev: EvaluatorParamsFormState
): EvaluatorParamsFormState {
  const defaults = createDefaultEvaluatorParamsForm(mode);
  const embedding =
    mode === 'openai-similarity' || mode === 'similarity'
      ? defaultEmbeddingModelForMode(mode)
      : prev.embedding_model_name || defaults.embedding_model_name;
  const metricId =
    mode === 'llm-judge' || mode === 'similarity' || mode === 'bert_score' || mode === 'openai-similarity'
      ? LLM_JUDGE_METRIC_OPTIONS.includes(prev.metric_id as LlmJudgeMetricId)
        ? prev.metric_id
        : defaults.metric_id
      : mode === 'algorithmic'
        ? defaultAlgorithmicMetric(prev.metric_id)
        : prev.metric_id.trim() || defaults.metric_id;
  const bertMetric = mode === 'bert_score' ? defaultBertScoreMetric(prev.metric_to_use) : prev.metric_to_use;
  const bertLang = mode === 'bert_score' ? defaultBertScoreLang(prev.lang) : prev.lang;
  const bertModel = mode === 'bert_score' ? defaultBertScoreModel(prev.model_type) : prev.model_type;
  return {
    ...defaults,
    ...prev,
    embedding_model_name: embedding,
    evaluation_scope: prev.evaluation_scope || defaults.evaluation_scope,
    metric_id: metricId,
    metric_to_use: bertMetric,
    lang: bertLang,
    model_type: bertModel,
  };
}

export function normalizeConfigTuningEvaluationMode(value: unknown): EvaluationMode {
  if (
    value === 'openai-similarity' ||
    value === 'similarity' ||
    value === 'bert_score' ||
    value === 'llm-judge' ||
    value === 'algorithmic' ||
    value === 'message-scorer' ||
    value === 'config-scorer'
  ) {
    return value;
  }
  return DEFAULT_EVALUATION_MODE;
}

export function normalizeEvaluationMode(value: unknown): EvaluationMode {
  return normalizeConfigTuningEvaluationMode(value);
}

export function isConfigTuningEvaluationMode(_mode: EvaluationMode): _mode is ConfigTuningEvaluationMode {
  return true;
}

export function parseEvaluatorParamsForm(raw: unknown): EvaluatorParamsFormState {
  const defaults = createDefaultEvaluatorParamsForm();
  if (!raw || typeof raw !== 'object') return defaults;
  const r = raw as Record<string, unknown>;
  const judge = r.judge && typeof r.judge === 'object' ? (r.judge as Record<string, unknown>) : {};

  return {
    embedding_model_name:
      typeof r.embedding_model_name === 'string' ? r.embedding_model_name : defaults.embedding_model_name,
    metric_to_use:
      typeof r.metric_to_use === 'string' ? defaultBertScoreMetric(r.metric_to_use) : defaults.metric_to_use,
    lang: typeof r.lang === 'string' ? defaultBertScoreLang(r.lang) : defaults.lang,
    model_type: typeof r.model_type === 'string' ? defaultBertScoreModel(r.model_type) : defaults.model_type,
    prompt: typeof r.prompt === 'string' ? r.prompt : defaults.prompt,
    evaluation_scope:
      r.evaluation_scope === 'dialog' || r.evaluation_scope === 'message'
        ? r.evaluation_scope
        : defaults.evaluation_scope,
    metric_id: typeof r.metric_id === 'string' && r.metric_id.trim() ? r.metric_id.trim() : defaults.metric_id,
    direction: r.direction === 'minimize' || r.direction === 'maximize' ? r.direction : defaults.direction,
    judge_temperature:
      judge.temperature != null && Number.isFinite(Number(judge.temperature))
        ? Math.min(1, Math.max(0, Number(judge.temperature)))
        : defaults.judge_temperature,
    judge_max_tokens: judge.max_tokens != null ? String(judge.max_tokens) : defaults.judge_max_tokens,
    judge_model_name: typeof judge.model_name === 'string' ? judge.model_name : defaults.judge_model_name,
    unit:
      r.unit === 'tokens' || r.unit === 'words' || r.unit === 'characters' || r.unit === 'message_count'
        ? r.unit
        : defaults.unit,
    aggregation:
      r.aggregation === 'mean' || r.aggregation === 'min' || r.aggregation === 'max' || r.aggregation === 'sum'
        ? r.aggregation
        : defaults.aggregation,
    token_model_name: typeof r.token_model_name === 'string' ? r.token_model_name : defaults.token_model_name,
    normalization_max: r.normalization_max != null ? String(r.normalization_max) : defaults.normalization_max,
    quality_drop_pct: r.quality_drop_pct != null ? String(r.quality_drop_pct) : defaults.quality_drop_pct,
  };
}

function appendStringField(target: Record<string, unknown>, key: string, value: string): void {
  const trimmed = value.trim();
  if (trimmed) target[key] = trimmed;
}

function appendNumberField(target: Record<string, unknown>, key: string, value: string, fallback?: number): void {
  const trimmed = value.trim();
  if (!trimmed) {
    if (fallback != null) target[key] = fallback;
    return;
  }
  const num = Number(trimmed);
  if (Number.isFinite(num)) target[key] = num;
}

/** Build API `evaluator_params` object for the given mode. */
export function buildEvaluatorParamsPayload(
  mode: EvaluationMode,
  form: EvaluatorParamsFormState,
  options?: { includeDirection?: boolean }
): Record<string, unknown> {
  const includeDirection = options?.includeDirection ?? false;
  const params: Record<string, unknown> = {};

  switch (mode) {
    case 'similarity':
      params.embedding_model_name = form.embedding_model_name.trim() || SIMILARITY_EMBEDDING_MODEL;
      appendStringField(params, 'metric_id', form.metric_id);
      if (includeDirection) params.direction = form.direction;
      // quality_drop_pct имеет смысл только когда отбор идёт по Token/Cost — на Quality ограничивать нечего.
      // Пустое поле подменяется дефолтом, а не выбрасывается: без процента профиль остаётся
      // single_metric по расходу, и тогда отклоняется ЛЮБОЕ улучшение качества, даже бесплатное —
      // алгоритм продолжает жать качество, а пул его не принимает, и прогон стоит на нулевой точке.
      if (includeDirection && (form.metric_id === 'token' || form.metric_id === 'cost')) {
        appendNumberField(params, 'quality_drop_pct', form.quality_drop_pct, DEFAULT_QUALITY_DROP_PCT);
      }
      break;
    case 'bert_score':
      params.metric_to_use = defaultBertScoreMetric(form.metric_to_use);
      params.lang = defaultBertScoreLang(form.lang);
      params.model_type = defaultBertScoreModel(form.model_type);
      appendStringField(params, 'metric_id', form.metric_id);
      if (includeDirection) params.direction = form.direction;
      if (includeDirection && (form.metric_id === 'token' || form.metric_id === 'cost')) {
        appendNumberField(params, 'quality_drop_pct', form.quality_drop_pct, DEFAULT_QUALITY_DROP_PCT);
      }
      break;
    case 'openai-similarity':
      params.embedding_model_name = form.embedding_model_name.trim() || OPENAI_SIMILARITY_EMBEDDING_MODEL;
      appendStringField(params, 'metric_id', form.metric_id);
      if (includeDirection) params.direction = form.direction;
      if (includeDirection && (form.metric_id === 'token' || form.metric_id === 'cost')) {
        appendNumberField(params, 'quality_drop_pct', form.quality_drop_pct, DEFAULT_QUALITY_DROP_PCT);
      }
      break;
    case 'llm-judge': {
      const prompt = form.prompt.trim();
      if (prompt) params.prompt = prompt;
      params.evaluation_scope = form.evaluation_scope;
      appendStringField(params, 'metric_id', form.metric_id);
      if (includeDirection) params.direction = form.direction;
      // quality_drop_pct имеет смысл только когда отбор идёт по Token/Cost — на Quality ограничивать нечего
      if (includeDirection && (form.metric_id === 'token' || form.metric_id === 'cost')) {
        appendNumberField(params, 'quality_drop_pct', form.quality_drop_pct, DEFAULT_QUALITY_DROP_PCT);
      }
      const judge: Record<string, unknown> = {};
      judge.temperature = form.judge_temperature;
      appendNumberField(judge, 'max_tokens', form.judge_max_tokens, 512);
      appendStringField(judge, 'model_name', form.judge_model_name);
      if (Object.keys(judge).length > 0) params.judge = judge;
      break;
    }
    case 'algorithmic': {
      params.evaluation_scope = form.evaluation_scope;
      params.unit = form.unit;
      params.aggregation = form.aggregation;
      params.metric_id = defaultAlgorithmicMetric(form.metric_id);
      if (includeDirection) params.direction = form.direction;
      appendStringField(params, 'token_model_name', form.token_model_name);
      appendNumberField(params, 'normalization_max', form.normalization_max, 500);
      break;
    }
    case 'message-scorer':
    case 'config-scorer':
      break;
    default:
      break;
  }

  return params;
}

export function validateEvaluatorParamsForm(mode: EvaluationMode, form: EvaluatorParamsFormState): string | null {
  if (mode === 'llm-judge' && !form.prompt.trim()) {
    return 'Judge prompt is required for LLM judge evaluation.';
  }
  if (mode === 'llm-judge' && !form.judge_model_name.trim()) {
    return 'Judge model is required for LLM judge evaluation.';
  }
  if (mode === 'algorithmic') {
    const max = Number(form.normalization_max);
    if (!Number.isFinite(max) || max <= 0) {
      return 'Normalization max must be a positive number for algorithmic evaluation.';
    }
    if (form.unit === 'tokens' && !form.token_model_name.trim()) {
      return 'Token model is required when unit is tokens.';
    }
  }
  return null;
}

export interface EvaluationRunRequestBody {
  config: Record<string, unknown>;
  dataset_name: OptimizationDatasetNamePayload;
  dataset_split: DatasetSplit;
  batch_size: number;
  evaluation_mode: EvaluationMode;
  evaluator_params: Record<string, unknown>;
}

export function buildEvaluationRunRequestBody(params: {
  config: Record<string, unknown>;
  dataset_names: string[];
  dataset_split: DatasetSplit;
  batch_size: number;
  evaluation_mode: EvaluationMode;
  evaluatorParamsForm: EvaluatorParamsFormState;
}): EvaluationRunRequestBody | null {
  const dataset_name = formatOptimizationDatasetNameForApi(params.dataset_names);
  if (!dataset_name) return null;

  return {
    config: params.config,
    dataset_name,
    dataset_split: params.dataset_split,
    batch_size: params.batch_size,
    evaluation_mode: params.evaluation_mode,
    evaluator_params: buildEvaluatorParamsPayload(params.evaluation_mode, params.evaluatorParamsForm, {
      includeDirection: false,
    }),
  };
}
